import { useMemo } from "react";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { useTranslation } from "@privacy/translations";
import { matchQuery, useSuspenseQuery } from "@tanstack/react-query";
import { Navigate, useNavigate } from "react-router";
import { usePrivacyDocumentTitle } from "../shared/usePrivacyDocumentTitle";
import { AutocompleteOption } from "../shared/Autocomplete";
import { userInfoKeys, userInfoQueryOptions } from "../api/userInfo";
import { queryClient } from "../api/queryClient";
import { LayoutWithoutSelectedOrganisation } from "./layout/Layout";

export const SelectOrganisationPage = () => {
  usePrivacyDocumentTitle();
  const userInfoQuery = useSuspenseQuery(userInfoQueryOptions());
  const organisations = userInfoQuery.data.customers;
  const { t, language } = useTranslation();
  const navigate = useNavigate();

  const sortedOrganizations = useMemo(() => {
    return [...organisations].sort((a, z) => a.name.localeCompare(z.name, language));
  }, [organisations, language]);

  if (organisations.length === 1) {
    const { slug } = organisations[0];
    return <Navigate to={`./${slug}`} replace />;
  }

  const handleOrganisationSelect = (organisationSlug: string) => () => {
    queryClient.resetQueries({ predicate: query => !matchQuery({ queryKey: userInfoKeys.user() }, query) });
    navigate(`./${organisationSlug}`);
  };

  return (
    <LayoutWithoutSelectedOrganisation>
      <Stack sx={{ alignItems: "center", justifyContent: "center", pt: "20vh" }}>
        <Stack spacing={2}>
          <Typography variant="h2" component="label" htmlFor="organisation-select">
            {t("SelectOrganisation")}
          </Typography>
          <Autocomplete
            noOptionsText={t("SelectOrganisation.empty")}
            id="organisation-select"
            options={sortedOrganizations}
            getOptionLabel={option => option.name}
            blurOnSelect
            value={null}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <AutocompleteOption
                  key={option.slug}
                  {...optionProps}
                  option={{ label: option.name, value: option.slug }}
                />
              );
            }}
            onChange={(_, selectedOrganisation) => {
              if (selectedOrganisation) {
                handleOrganisationSelect(selectedOrganisation.slug)();
              }
            }}
            sx={{ minWidth: "20rem" }}
            disabled={organisations.length === 0}
            renderInput={params => (
              <TextField
                autoFocus
                {...params}
                slotProps={{
                  ...params.slotProps,
                  input: { ...params.slotProps.input },
                }}
              />
            )}
          />
        </Stack>
      </Stack>
    </LayoutWithoutSelectedOrganisation>
  );
};
